// Voz para "Preguntale a tu obra": escuchar (dictado) y hablar (leer la respuesta).
//
// Escuchar tiene dos caminos:
//  - 'sr': reconocimiento del navegador (Chrome/Edge: webkitSpeechRecognition). Texto en vivo mientras habla.
//  - 'rec': grabación propia (getUserMedia + MediaRecorder) con detección de voz por volumen; al quedarse
//    callado ~1,2 s se corta y se manda al servidor (/api/obra-chat/stt) para pasarla a texto. Funciona en
//    cualquier navegador moderno y es el plan B si el del navegador falla (error 'network', etc.).
// Siempre se abre el micrófono con getUserMedia primero: así aparece el pedido de permiso y los errores
// (bloqueado, sin micrófono) se pueden explicar. El mismo stream alimenta el medidor de volumen.
// Micrófono mudo: si el predeterminado de Windows no manda sonido (pasó en la PC de Jairo con una interfaz
// de audio sin señal), se prueban los otros y se usa el primero que suena. Como el dictado de Chrome usa
// siempre el predeterminado, en ese caso se pasa a grabación propia + servidor.

const log = (...a) => { try { console.info('[obra-voz]', ...a); } catch (e) { /* */ } };

export const SR = typeof window !== 'undefined' ? (window.SpeechRecognition || window.webkitSpeechRecognition || null) : null;
export const TTS = typeof window !== 'undefined' && 'speechSynthesis' in window && typeof window.SpeechSynthesisUtterance !== 'undefined';
export const PUEDE_GRABAR = typeof window !== 'undefined' && !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia) && typeof window.MediaRecorder !== 'undefined';

export const MENSAJES = {
  inseguro: 'El micrófono solo funciona en una página segura (https o localhost).',
  sinSoporte: 'Tu navegador no deja usar el micrófono: abrí LisualPro en Chrome o Edge, o escribí la pregunta.',
  bloqueado: 'Bloqueaste el micrófono para esta página: tocá el candado de la barra de direcciones, permití el micrófono y probá de nuevo.',
  sinMic: 'No encuentro ningún micrófono conectado. Revisá que esté enchufado y habilitado en Windows.',
  ocupado: 'Otro programa está usando el micrófono (Zoom, Meet, Teams…). Cerralo y probá de nuevo.',
  noOi: 'No te escuché. Tocá el micrófono y hablá un poco más fuerte.',
  red: 'El dictado de Chrome no responde; sigo con el dictado de Lisual.',
  sttFallo: 'No pude pasar el audio a texto. Probá de nuevo o escribí la pregunta.',
  mudo: 'Tu micrófono no manda sonido. Revisá que no esté silenciado en Windows (Configuración › Sonido › Entrada) o elegí otro micrófono abajo.',
};

const errorDeGetUserMedia = (e) => {
  const n = (e && e.name) || '';
  if (n === 'NotAllowedError' || n === 'SecurityError' || n === 'PermissionDeniedError') return 'bloqueado';
  if (n === 'NotFoundError' || n === 'DevicesNotFoundError' || n === 'OverconstrainedError') return 'sinMic';
  if (n === 'NotReadableError' || n === 'TrackStartError' || n === 'AbortError') return 'ocupado';
  return 'sinSoporte';
};

const MIC_KEY = 'obraVoz:mic';
const micGuardado = () => { try { return localStorage.getItem(MIC_KEY) || ''; } catch (e) { return ''; } };
export const elegirMic = (id) => { try { if (id) localStorage.setItem(MIC_KEY, id); else localStorage.removeItem(MIC_KEY); } catch (e) { /* */ } };
const esAlias = (d) => d.deviceId === 'default' || d.deviceId === 'communications';

/** Micrófonos disponibles (con nombre recién después de dar permiso). */
export async function listarMics() {
  try {
    const ds = await navigator.mediaDevices.enumerateDevices();
    return ds.filter((d) => d.kind === 'audioinput' && !esAlias(d)).map((d) => ({ id: d.deviceId, nombre: d.label || 'Micrófono' }));
  } catch (e) { return []; }
}

// Nivel máximo de un stream durante ms milisegundos (0 = silencio digital: el micrófono no manda nada).
async function nivelMax(stream, ms) {
  const AC = window.AudioContext || window.webkitAudioContext;
  const c = new AC();
  try {
    if (c.state === 'suspended') await c.resume();
    const an = c.createAnalyser();
    an.fftSize = 2048;
    c.createMediaStreamSource(stream).connect(an);
    const b = new Float32Array(1024);
    let mx = 0;
    const t0 = Date.now();
    while (Date.now() - t0 < ms) {
      await new Promise((r) => setTimeout(r, 50));
      an.getFloatTimeDomainData(b);
      for (let i = 0; i < b.length; i++) { const v = Math.abs(b[i]); if (v > mx) mx = v; }
    }
    return mx;
  } finally { try { c.close(); } catch (e) { /* */ } }
}
const AUDIO = { echoCancellation: true, noiseSuppression: true, autoGainControl: true };

function mejorMime() {
  const ops = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4'];
  for (const m of ops) { try { if (window.MediaRecorder.isTypeSupported(m)) return m; } catch (e) { /* */ } }
  return '';
}

/**
 * Micrófono de la charla. cb: { onState(estado), onInterim(texto), onFinal(texto), onError(clave, detalle), onLevel(0..1), onModo(modo) }
 * estado: 'abriendo' | 'escuchando' | 'hablando' (detectó voz) | 'procesando' | 'quieto'
 * transcribir(blob) -> Promise<string>  (camino 'rec')
 */
export function crearMicrofono(cb, transcribir) {
  let stream = null, ctx = null, analyser = null, levelTimer = null;
  let modo = SR ? 'sr' : 'rec';
  let fuente = null;
  // El AudioContext se crea/reanuda en el toque del micrófono (gesto del usuario): si no, Chrome lo deja
  // suspendido y el medidor y el detector de voz dan 0. Se reusa entre aperturas.
  function prepararAudio() {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!ctx || ctx.state === 'closed') ctx = new AC();
      if (ctx.state === 'suspended') ctx.resume();
    } catch (e) { log('sin AudioContext', e && e.message); }
  }
  let rec = null, mr = null, activo = false, entregado = false;
  const buf = new Float32Array(1024);
  let nivel = 0, piso = 0.01;

  const medir = () => {
    if (!analyser) return 0;
    analyser.getFloatTimeDomainData(buf);
    let s = 0;
    for (let i = 0; i < buf.length; i++) s += buf[i] * buf[i];
    return Math.sqrt(s / buf.length);
  };

  let abriendo = null;
  function abrir() {
    if (stream) return Promise.resolve(true);
    if (!abriendo) abriendo = abrirMic().finally(() => { abriendo = null; });
    return abriendo;
  }
  // Abre el micrófono trabajando con variables locales: el stream recién se guarda al final, y si mientras
  // tanto se cerró la charla (cerrar() sube "gen"), se apaga lo abierto y no se toca nada compartido.
  let gen = 0;
  async function abrirMic() {
    if (stream) return true;
    const g = gen;
    const vigente = () => g === gen;
    const apagar = (st) => { try { if (st) st.getTracks().forEach((t) => t.stop()); } catch (e) { /* */ } };
    if (typeof window !== 'undefined' && window.isSecureContext === false) { cb.onError('inseguro'); return false; }
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      if (SR) return true; // sin getUserMedia pero con SR: que lo intente igual
      cb.onError('sinSoporte'); return false;
    }
    const elegido = micGuardado();
    let st = null;
    try {
      try {
        st = await navigator.mediaDevices.getUserMedia({ audio: elegido ? { ...AUDIO, deviceId: { exact: elegido } } : AUDIO });
      } catch (e) {
        if (!elegido || (e && e.name === 'NotAllowedError')) throw e;
        log('el micrófono elegido ya no está, uso el predeterminado'); elegirMic('');
        st = await navigator.mediaDevices.getUserMedia({ audio: AUDIO });
      }
      log('micrófono abierto', st.getAudioTracks().map((t) => t.label).join(', '));
    } catch (e) {
      log('getUserMedia falló', e && e.name, e && e.message);
      if (vigente()) cb.onError(errorDeGetUserMedia(e), e && e.name);
      return false;
    }
    if (!vigente()) { apagar(st); return false; }
    try {
      // ¿Manda sonido? Un micrófono andando nunca da silencio digital exacto (siempre hay algo de ruido).
      let track = st.getAudioTracks()[0];
      const mx = await nivelMax(st, 900);
      if (!vigente()) { apagar(st); return false; }
      log('nivel de prueba', track && track.label, mx.toFixed(5));
      if (mx < 1e-5) {
        const mudo = track ? track.label : '';
        const idMudo = track ? track.getSettings().deviceId : '';
        const otros = (await listarMics()).filter((d) => d.nombre !== mudo && d.id !== idMudo);
        let otro = null;
        for (const d of otros) {
          if (!vigente()) break;
          let prueba = null;
          try {
            prueba = await navigator.mediaDevices.getUserMedia({ audio: { ...AUDIO, deviceId: { exact: d.id } } });
            const n = await nivelMax(prueba, 700);
            log('probando', d.nombre, n.toFixed(5));
            if (n >= 1e-5) { otro = { d, st: prueba }; break; }
          } catch (e) { log('no se pudo probar', d.nombre, e && e.name); }
          apagar(prueba);
        }
        apagar(st);
        if (!vigente()) { if (otro) apagar(otro.st); return false; }
        if (!otro) { cb.onError('mudo', mudo); return false; }
        st = otro.st;
        track = st.getAudioTracks()[0];
        elegirMic(otro.d.id);
        if (cb.onAviso) cb.onAviso(`El micrófono "${mudo || 'predeterminado'}" no manda sonido: uso "${otro.d.nombre}".`);
      }
      // El dictado de Chrome escucha siempre el micrófono predeterminado de Windows: si estamos usando otro, grabación propia.
      const usado = track ? track.getSettings().deviceId : '';
      const pred = (await navigator.mediaDevices.enumerateDevices()).find((d) => d.kind === 'audioinput' && d.deviceId === 'default');
      if (!vigente()) { apagar(st); return false; }
      const set = track && track.getSettings ? track.getSettings() : {};
      const nom = String((track && track.label) || '').toLowerCase();
      const esPred = !usado || usado === 'default' || !pred || (pred.groupId && set.groupId === pred.groupId) || (!!nom && String(pred.label || '').toLowerCase().includes(nom));
      // Predeterminado = dictado del navegador (palabras en vivo); si no, grabación propia + servidor.
      modo = SR && esPred ? 'sr' : PUEDE_GRABAR && transcribir ? 'rec' : 'sr';
      log('modo', modo === 'sr' ? 'dictado en vivo' : 'grabación + servidor', '· predeterminado:', esPred);
      if (cb.onModo) cb.onModo(modo);
      if (cb.onMic) cb.onMic({ id: usado, nombre: track ? track.label : '' });
      stream = st; // recién acá queda como el micrófono de la charla
    } catch (e) {
      log('falló la prueba del micrófono', e && e.name, e && e.message);
      apagar(st);
      if (vigente()) cb.onError('sinSoporte', e && e.name);
      return false;
    }
    try {
      prepararAudio();
      const c = ctx;
      if (c.state === 'suspended') await c.resume();
      if (!vigente() || !stream) return !!stream;
      fuente = c.createMediaStreamSource(stream);
      analyser = c.createAnalyser();
      analyser.fftSize = 2048;
      fuente.connect(analyser);
      clearInterval(levelTimer);
      levelTimer = setInterval(() => { nivel = medir(); if (cb.onLevel) cb.onLevel(Math.min(1, nivel * 12)); }, 60);
      log('AudioContext', c.state);
    } catch (e) { log('sin medidor de volumen', e && e.message); }
    return !!stream;
  }

  function cerrar() {
    gen += 1; // invalida cualquier abrirMic() en curso
    activo = false;
    clearInterval(levelTimer); levelTimer = null;
    try { if (rec) rec.abort(); } catch (e) { /* */ }
    try { if (mr && mr.state !== 'inactive') mr.stop(); } catch (e) { /* */ }
    rec = null; mr = null;
    if (stream) stream.getTracks().forEach((t) => t.stop());
    stream = null;
    try { if (fuente) fuente.disconnect(); } catch (e) { /* */ }
    fuente = null; analyser = null; // el AudioContext se reusa (nació del gesto)
    if (cb.onLevel) cb.onLevel(0);
    cb.onState('quieto');
  }

  const entregar = (texto) => {
    if (entregado) return;
    entregado = true; activo = false;
    cb.onState('quieto');
    cb.onFinal(String(texto || '').trim());
  };

  // --- Camino 1: reconocimiento del navegador ---
  function escucharSR() {
    let fin = '', ultimo = '', reinicios = 0, timer = null;
    const cortar = () => { clearTimeout(timer); timer = null; const r = rec; rec = null; try { if (r) r.stop(); } catch (e) { /* */ } entregar(ultimo); };
    const arrancar = () => {
      const r = new SR();
      rec = r;
      r.lang = 'es-AR';
      r.interimResults = true;
      r.continuous = true; // charla: escucha seguido y se manda al callarse ~1,3 s
      r.maxAlternatives = 1;
      let huboError = null;
      r.onstart = () => { log('SR onstart'); cb.onState('escuchando'); };
      r.onspeechstart = () => { log('SR habla'); cb.onState('hablando'); };
      r.onresult = (ev) => {
        let f = '', interim = '';
        for (let i = 0; i < ev.results.length; i++) {
          const t = ev.results[i][0].transcript;
          if (ev.results[i].isFinal) f += t; else interim += t;
        }
        ultimo = (fin + ' ' + f + ' ' + interim).replace(/\s+/g, ' ').trim();
        if (ultimo) { cb.onState('hablando'); cb.onInterim(ultimo); clearTimeout(timer); timer = setTimeout(cortar, 1300); }
      };
      r.onerror = (ev) => {
        huboError = ev.error;
        log('SR onerror', ev.error, ev.message || '');
        if (ev.error === 'not-allowed') cb.onError('bloqueado', ev.error);
        else if (ev.error === 'audio-capture') cb.onError('sinMic', ev.error);
      };
      r.onend = () => {
        log('SR onend', { ultimo, huboError });
        if (rec !== r) return;
        rec = null;
        if (!activo || entregado) return;
        if (['network', 'service-not-allowed', 'language-not-supported'].includes(huboError) && PUEDE_GRABAR && stream && transcribir) {
          // El servicio de Chrome no está disponible (red, política, navegador sin claves): plan B.
          modo = 'rec';
          if (cb.onModo) cb.onModo(modo);
          cb.onError('red', huboError);
          escucharRec();
          return;
        }
        if (huboError === 'not-allowed' || huboError === 'audio-capture') { activo = false; cb.onState('quieto'); return; }
        if (ultimo) { cortar(); return; }
        // Chrome corta solo tras un rato de silencio: se reinicia sin que se note (hasta ~1 minuto).
        if (++reinicios <= 6) { fin = ''; try { arrancar(); } catch (e) { entregar(''); } return; }
        entregar('');
      };
      try {
        r.start();
        log('SR start()');
      } catch (e) {
        log('SR start() tiró', e && e.name, e && e.message);
        rec = null;
        if (PUEDE_GRABAR && stream && transcribir) { modo = 'rec'; if (cb.onModo) cb.onModo(modo); escucharRec(); } else { cb.onError('sinSoporte', e && e.name); activo = false; cb.onState('quieto'); }
      }
    };
    arrancar();
  }

  // --- Camino 2: grabación propia con corte por silencio ---
  function escucharRec() {
    if (!stream || typeof window.MediaRecorder === 'undefined') { cb.onError('sinSoporte'); activo = false; cb.onState('quieto'); return; }
    const mime = mejorMime();
    let m;
    try { m = mime ? new window.MediaRecorder(stream, { mimeType: mime }) : new window.MediaRecorder(stream); } catch (e) { cb.onError('sinSoporte', e && e.name); activo = false; cb.onState('quieto'); return; }
    mr = m;
    const partes = [];
    let hablo = false, forzado = false, ultimoSonido = 0, maxV = 0, ultLog = 0;
    const t0 = Date.now();
    // Piso de ruido de los primeros 300 ms: el umbral se adapta a la sala.
    const muestras = [];
    m.ondataavailable = (e) => { if (e.data && e.data.size) partes.push(e.data); };
    m.onstop = async () => {
      clearInterval(vad);
      if (mr !== m) return;
      mr = null;
      if (!activo) return;
      if (!hablo && !forzado) { log('sin voz: no se manda'); cb.onError('noOi'); entregar(''); return; }
      cb.onState('procesando');
      try {
        const blob = new Blob(partes, { type: m.mimeType || mime || 'audio/webm' });
        log('grabación lista', Math.round(blob.size / 1024) + ' KB', blob.type);
        const texto = await transcribir(blob);
        log('transcripción', texto);
        if (texto) cb.onInterim(texto);
        entregar(texto);
      } catch (e) {
        log('stt falló', e && e.message);
        cb.onError('sttFallo', e && e.message); activo = false; cb.onState('quieto');
      }
    };
    const vad = setInterval(() => {
      const v = medir();
      const t = Date.now() - t0;
      if (t < 500) { muestras.push(v); return; } // piso de ruido de la sala
      if (muestras.length) { piso = Math.max(0.001, muestras.reduce((a, b) => a + b, 0) / muestras.length); muestras.length = 0; log('piso de ruido', piso.toFixed(4)); }
      const umbral = Math.max(0.004, piso * 3);
      if (v > maxV) maxV = v;
      if (Date.now() - ultLog > 1000) { ultLog = Date.now(); log('nivel', v.toFixed(4), 'máx', maxV.toFixed(4), 'umbral', umbral.toFixed(4), hablo ? '(hablando)' : ''); }
      if (v > umbral) { if (!hablo) { hablo = true; cb.onState('hablando'); } ultimoSonido = Date.now(); }
      const callado = hablo && Date.now() - ultimoSonido > 1200;
      if (!hablo && t > 8000) {
        // Respaldo: hubo señal (el medidor se movió) aunque no pasó el umbral: se manda igual.
        forzado = maxV > piso * 1.6 && maxV > 0.002;
        log(forzado ? 'no detecté voz pero hubo señal: mando igual' : 'silencio');
      }
      if (callado || t > 20000 || (!hablo && t > 8000)) { try { m.stop(); } catch (e) { /* */ } }
    }, 50);
    try { m.start(250); cb.onState('escuchando'); log('grabando', m.mimeType); } catch (e) { clearInterval(vad); cb.onError('sinSoporte', e && e.name); activo = false; cb.onState('quieto'); }
  }

  return {
    get modo() { return modo; },
    get abierto() { return !!stream; },
    nivel: () => nivel,
    async escuchar() {
      if (activo) return;
      prepararAudio(); // sincrónico, dentro del toque del usuario
      try {
        cb.onState('abriendo');
        const g = gen;
        const ok = await abrir();
        if (!ok || g !== gen || !stream && !(SR && modo === 'sr')) { cb.onState('quieto'); return; }
        activo = true; entregado = false;
        cb.onInterim('');
        if (modo === 'sr' && SR) escucharSR(); else escucharRec();
      } catch (e) {
        // Nunca romper la pantalla: cualquier falla del micrófono queda como aviso.
        log('escuchar falló', e && e.name, e && e.message);
        activo = false;
        cb.onError('sinSoporte', e && e.message);
        cb.onState('quieto');
      }
    },
    // Termina de escuchar ya y manda lo que haya (tocar el micrófono mientras escucha).
    terminar() {
      if (rec) { try { rec.stop(); } catch (e) { /* */ } }
      if (mr && mr.state !== 'inactive') { try { mr.stop(); } catch (e) { /* */ } }
    },
    cancelar() { activo = false; try { if (rec) rec.abort(); } catch (e) { /* */ } rec = null; try { if (mr && mr.state !== 'inactive') mr.stop(); } catch (e) { /* */ } mr = null; cb.onState('quieto'); },
    cerrar,
    // Cambiar de micrófono: se cierra y la próxima vez abre el elegido.
    cambiarMic(id) { elegirMic(id); cerrar(); },
  };
}

// ---------------------------------------------------------------------------
// Hablar: voz en castellano, de a una oración (Chrome corta las frases largas a los ~15 s).
// ---------------------------------------------------------------------------
let voces = [];
const cargarVoces = () => { try { voces = window.speechSynthesis.getVoices() || []; } catch (e) { voces = []; } };
if (TTS) { cargarVoces(); try { window.speechSynthesis.addEventListener('voiceschanged', cargarVoces); } catch (e) { /* */ } }

export function mejorVoz() {
  if (!voces.length) cargarVoces();
  const es = voces.filter((v) => /^es([-_]|$)/i.test(v.lang));
  const buena = (v) => (/google|neural|natural|online/i.test(v.name) ? 1 : 0);
  const rango = (v) => (/es[-_]AR/i.test(v.lang) ? 4 : /es[-_](419|US|MX|CO|CL|UY)/i.test(v.lang) ? 3 : /es[-_]ES/i.test(v.lang) ? 2 : 1);
  return es.sort((a, b) => rango(b) * 2 + buena(b) - (rango(a) * 2 + buena(a)))[0] || null;
}

let hablando = null;
const vivas = new Set(); // referencias a las frases en curso (si no, Chrome puede perder el onend)
export function callar() {
  hablando = null;
  if (TTS) try { window.speechSynthesis.cancel(); } catch (e) { /* */ }
}

/** Lee el texto; onFin se llama al terminar (no si se interrumpe con callar()). */
export function hablar(texto, onFin) {
  if (!TTS || !texto) { if (onFin) onFin(); return; }
  callar();
  const s = window.speechSynthesis;
  const voz = mejorVoz();
  const frases = String(texto).replace(/\s+/g, ' ').match(/[^.!?¿¡]+[.!?]*|[¿¡][^?!]+[?!]/g) || [texto];
  const id = {};
  hablando = id;
  let i = 0;
  const siguiente = () => {
    if (hablando !== id) return;
    if (i >= frases.length) { hablando = null; if (onFin) onFin(); return; }
    const u = new window.SpeechSynthesisUtterance(frases[i++].trim());
    if (voz) u.voice = voz;
    u.lang = voz ? voz.lang : 'es-AR';
    u.rate = 1.05;
    // Chrome a veces no dispara onend (la frase se pierde por el recolector o la voz de Google se corta):
    // se guarda la referencia y hay un reloj de respaldo según el largo de la frase.
    let listo = false;
    const fin = () => { if (listo) return; listo = true; clearTimeout(reloj); vivas.delete(u); siguiente(); };
    const reloj = setTimeout(() => { log('tts sin onend, sigo'); fin(); }, 2500 + u.text.length * 110);
    vivas.add(u);
    u.onend = fin;
    u.onerror = (e) => { log('tts error', e && e.error); if (e && (e.error === 'interrupted' || e.error === 'canceled')) { listo = true; clearTimeout(reloj); vivas.delete(u); return; } fin(); };
    if (s.paused) s.resume();
    s.speak(u);
  };
  log('hablar con', voz ? `${voz.name} (${voz.lang})` : 'voz por defecto');
  siguiente();
}
export const estaHablando = () => !!hablando;
